import React from 'react';
import { useNavigate } from 'react-router-dom';

const Hero: React.FC = () => {
    const navigate = useNavigate();

    const handleReadMore = () => {
        navigate('/about');
    };

    const sdgGoals = [
        {
            id: 1,
            img: "sd1.png",
            color: "#E5243B",
            title: "No Poverty",
            url: "https://www.un.org/sustainabledevelopment/poverty/"
        },
        {
            id: 2,
            img: "sd2.png",
            color: "#DDA63A",
            title: "Zero Hunger",
            url: "https://www.un.org/sustainabledevelopment/hunger/"
        },
        {
            id: 3,
            img: "sd3.png",
            color: "#4C9F38",
            title: "Good Health",
            url: "https://www.un.org/sustainabledevelopment/health/"
        },
        {
            id: 4,
            img: "sd4.png",
            color: "#C5192D",
            title: "Quality Education",
            url: "https://www.un.org/sustainabledevelopment/education/"
        },
        {
            id: 5,
            img: "sd5.png",
            color: "#FF3A21",
            title: "Gender Equality",
            url: "https://www.un.org/sustainabledevelopment/gender-equality/"
        },
        {
            id: 8,
            img: "sd8.png",
            color: "#A21942",
            title: "Economic Growth",
            url: "https://www.un.org/sustainabledevelopment/economic-growth/"
        }
    ];

    return (
        <section id="home" className="relative w-full max-w-full pt-4 sm:pt-6 pb-14 md:pb-20 lg:pb-24 bg-white overflow-hidden">
            {/* Subtle background glow */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-10 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* 1. Centered SDG Goals Row */}
                <div className="flex justify-center items-center pt-2 pb-6 sm:pb-8">
                    <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 md:gap-4">
                        {sdgGoals.map((goal) => (
                            <a
                                key={goal.id}
                                href={goal.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded overflow-hidden shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200 block"
                                title={goal.title}
                            >
                                <img
                                    src={`/images/${goal.img}`}
                                    alt={goal.title}
                                    className="w-full h-full object-cover"
                                />
                            </a>
                        ))}
                    </div>
                </div>

                {/* 2. Hero Content: Two Columns */}
                <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
                    {/* Left Column: Text & Checklist */}
                    <div className="w-full lg:w-1/2 text-center lg:text-left lg:pl-6">
                        <div className="inline-block mb-4 sm:mb-5">
                            <span className="px-4 py-1.5 bg-[#E6F4F5] text-[#0A6970] rounded-full text-xs sm:text-sm font-semibold tracking-wide">
                                Empowering Communities
                            </span>
                        </div>

                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[50px] font-extrabold text-[#111827] leading-[1.16] mb-5 tracking-tight font-poppins">
                            Building a <span className="text-[#0E6C75]">Better</span>
                            <br />
                            <span className="text-[#1A3340]">Future</span> through
                            <br />
                            <span className="text-[#0E6C75]">Care, Opportunity,</span>
                            <br />
                            <span className="text-[#4D6331]">and Empowerment</span>
                        </h1>

                        <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                            Lakshmi Venkatesan Charitable Trust is a non-profit organization working to uplift communities through education, healthcare, and women empowerment.
                        </p>

                        {/* Checklist with dark green badges */}
                        <div className="space-y-3.5 mb-8 max-w-md mx-auto lg:mx-0 text-left">
                            {[
                                'Empowering Entrepreneurs',
                                'Structured Mentorship Program',
                                'Long Term Commitment',
                                'Impact on Job Creation'
                            ].map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center space-x-3.5 text-gray-800 font-semibold text-sm sm:text-base group cursor-default"
                                >
                                    <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#3B4D2B] flex items-center justify-center shadow-sm">
                                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                            <button
                                onClick={handleReadMore}
                                className="inline-flex items-center justify-center bg-secondary hover:bg-secondary-dark text-white text-base font-semibold px-8 py-3.5 rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
                            >
                                Read More
                            </button>
                            <button
                                onClick={() => navigate('/support-request')}
                                className="inline-flex items-center justify-center bg-[#0E6C75] hover:bg-[#0A545C] text-white text-base font-semibold px-8 py-3.5 rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
                            >
                                Request Support
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Hero Image with Floating Badges */}
                    <div className="w-full lg:w-1/2 flex justify-center items-center">
                        <div className="relative w-full max-w-[500px] lg:max-w-[560px]">
                            {/* Card Frame with heroimg.png */}
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100 bg-white">
                                <img
                                    src="/images/heroimg.png"
                                    alt="Empowering Communities - Women's Mentorship, Education For All, Healthcare Support"
                                    className="w-full h-auto object-cover block"
                                />

                                {/* 800+ Lives Impacted Badge (Top Right) */}
                                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-white/95 backdrop-blur-md px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl shadow-lg border border-gray-100 text-center">
                                    <span className="block text-xl sm:text-2xl font-extrabold text-[#0E6C75] leading-none">
                                        800+
                                    </span>
                                    <span className="block text-[10px] sm:text-xs text-gray-500 font-medium mt-1">
                                        Lives Impacted
                                    </span>
                                </div>

                                {/* Floating Live Impact Card (Bottom Left) */}
                                <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-white/95 backdrop-blur-md px-4 sm:px-5 py-3 rounded-xl shadow-xl border border-gray-100 max-w-[240px] sm:max-w-[280px]">
                                    <div className="flex items-center space-x-1.5 mb-1">
                                        <span className="relative flex h-2.5 w-2.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                        </span>
                                        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-700">
                                            Live Impact
                                        </span>
                                    </div>
                                    <h4 className="text-xs sm:text-sm font-bold text-gray-900 leading-snug">
                                        Empowering Communities
                                    </h4>
                                    <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight mt-0.5">
                                        Through Education, Health &amp; Livelihood
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
